// FirewallVpnTunnels.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Wifi, ArrowDown, ArrowUp,
    Clock, Hash, ChevronDown,
    ChevronUp, Activity, Layers
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";


// ── Types ─────────────────────────────────────────────────────────────────────
interface ProxyEndpoint {
    port: number;
    subnet: string;
    protocol: number;
    protocol_name: string;
}

interface ProxyId {
    expire: number;
    p2name: string;
    status: string;
    p2serial: number;
    proxy_dst: ProxyEndpoint[];
    proxy_src: ProxyEndpoint[];
    incoming_bytes: number;
    outgoing_bytes: number;
}

interface TunnelData {
    name: string;
    rgwy: string;
    type: string;
    tun_id: string;
    proxyid: ProxyId[];
    tun_id6: string;
    bytes_in: number;
    comments: string;
    peer_ips: ProxyEndpoint[][];
    username: string;
    bytes_out: number;
    bytes_in_gb: number;
    bytes_in_mb: number;
    "wizard-type": string;
    bytes_out_gb: number;
    bytes_out_mb: number;
    creation_time: number;
    incoming_bytes: number;
    outgoing_bytes: number;
    connection_count: number;
    status: string;
}

interface VpnTunnelItem {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    name: string;
    status: string | null;
    data: any;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseData = (raw: any): TunnelData | null => {
    if (typeof raw === "object" && raw !== null) {
        return raw as TunnelData;
    }
    try { return JSON.parse(raw); } catch { return null; }
};

const fmtBytes = (bytes: number): string => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024, sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const fmtUptime = (seconds: number): string => {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const parts = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    return parts.join(" ") || "< 1m";
};

// ── Sub-components ─────────────────────────────────────────────────────────────
const StatCard = ({
    icon: Icon, label, value, sub, iconColor, bgColor
}: {
    icon: any; label: string; value: string; sub?: string;
    iconColor: string; bgColor: string;
}) => (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${bgColor}`}>
        <div className={`p-2 rounded-lg ${bgColor} border ${bgColor.replace("bg-", "border-")}`}>
            <Icon size={16} className={iconColor} />
        </div>
        <div className="min-w-0">
            <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest truncate">{label}</p>
            <p className={`text-sm font-black ${iconColor} leading-tight`}>{value}</p>
            {sub && <p className="text-[9px] text-gray-200 mt-0.5">{sub}</p>}
        </div>
    </div>
);

const SubnetChip = ({ subnet, label }: { subnet: string; label: "src" | "dst" }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-mono font-bold border ${label === "src"
        ? "bg-blue-500/10 border-blue-500/20 text-blue-300"
        : "bg-orange-500/10 border-orange-500/20 text-orange-300"
        }`}>
        {subnet}
    </span>
);

function ProxyIdCard({ proxy }: { proxy: ProxyId; }) {
    const [expanded, setExpanded] = useState(true);

    return (
        <div className="bg-gray-900/60 border border-gray-700/60 rounded-xl overflow-hidden">
            {/* Proxy Header */}
            <button
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-700/30 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-purple-600/20 border border-purple-500/30 flex items-center justify-center shrink-0">
                        <Layers size={13} className="text-purple-400" />
                    </div>
                    <div className="text-left">
                        <p className="text-[12px] font-bold text-white">{proxy.p2name}</p>
                        <p className="text-[9px] text-gray-200">Phase 2 SA · Serial #{proxy.p2serial}</p>
                    </div>
                    {proxy.status ? (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border ml-2 ${proxy.status === "up"
                            ? "bg-green-500/10 border-green-500/20 text-green-400"
                            : "bg-red-500/10 border-red-500/20 text-red-400"
                            }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${proxy.status === "up" ? "bg-green-400" : "bg-red-400"}`} />
                            {proxy.status.toUpperCase()}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border ml-2 bg-gray-500/10 border-gray-500/20 text-gray-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                            -
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 text-right">
                        <div>
                            <p className="text-[9px] text-gray-200">Expires</p>
                            {proxy.expire ? (
                                <p className="text-[10px] font-bold text-yellow-400">{proxy.expire}s</p>
                            ) : (
                                <p className="text-[10px] font-bold text-yellow-400">-</p>
                            )}
                        </div>
                        <div>
                            <p className="text-[9px] text-gray-200">In</p>
                            {proxy.incoming_bytes ? (
                                <p className="text-[10px] font-bold text-blue-400">{fmtBytes(proxy.incoming_bytes)}</p>
                            ) : (
                                <p className="text-[10px] font-bold text-blue-400">-</p>
                            )}
                        </div>
                        <div>
                            <p className="text-[9px] text-gray-200">Out</p>
                            {proxy.outgoing_bytes ? (
                                <p className="text-[10px] font-bold text-orange-400">{fmtBytes(proxy.outgoing_bytes)}</p>
                            ) : (
                                <p className="text-[10px] font-bold text-orange-400">-</p>
                            )}
                        </div>
                    </div>
                    {expanded ? <ChevronUp size={14} className="text-gray-200" /> : <ChevronDown size={14} className="text-gray-200" />}
                </div>
            </button>

            {expanded && (
                <div className="px-4 pb-4 border-t border-gray-700/40">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                        {/* Source Subnets */}
                        <div>
                            <div className="flex items-center gap-1.5 mb-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest">Source Subnets</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {proxy.proxy_src.map((s, i) => (
                                    <SubnetChip key={i} subnet={s.subnet} label="src" />
                                ))}
                            </div>
                        </div>
                        {/* Destination Subnets */}
                        <div>
                            <div className="flex items-center gap-1.5 mb-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                                <p className="text-[9px] font-bold text-orange-400 uppercase tracking-widest">Destination Subnets</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {proxy.proxy_dst.map((d, i) => (
                                    <SubnetChip key={i} subnet={d.subnet} label="dst" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TunnelCard({ item }: { item: VpnTunnelItem }) {
    const d = parseData(item.data);
    if (!d) return null;

    const totalBytes = d.bytes_in + d.bytes_out;
    const rxPct = totalBytes > 0 ? (d.bytes_in / totalBytes) * 100 : 0;
    const txPct = totalBytes > 0 ? (d.bytes_out / totalBytes) * 100 : 0;

    return (
        <div className="bg-gray-800/40 border border-gray-700 rounded-2xl overflow-hidden shadow-2xl">

            {/* Card Header */}
            <div className="bg-gray-800/60 px-5 py-4 border-b border-gray-700/60 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-600/20 border border-purple-500/30 flex items-center justify-center shrink-0">
                        <Wifi size={18} className="text-purple-400" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-base font-black text-white">{d.name}</h2>

                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold border bg-purple-500/10 border-purple-500/20 text-purple-400 uppercase">
                                {d.type}
                            </span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold border bg-gray-700/60 border-gray-600/40 text-gray-200 uppercase">
                                {d["wizard-type"]}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${d.status === "up" ? "bg-green-500/10 border-green-500/20 text-green-400" : "bg-red-500/10 border-red-500/20 text-red-400"} uppercase`}>
                                {d.status}
                            </span>
                        </div>
                        {d.comments && <p className="text-[10px] text-gray-200 mt-0.5">{d.comments}</p>}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-[9px] text-gray-200 uppercase tracking-widest">Remote Gateway</p>
                    <p className="font-mono text-sm font-black text-cyan-300">{d.rgwy}</p>
                </div>
            </div>

            <div className="p-5 space-y-5">

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                        icon={ArrowDown} label="Total Inbound" value={fmtBytes(d.bytes_in)}
                        // sub={`${d.bytes_in_gb} GB`}
                        iconColor="text-blue-400" bgColor="bg-blue-500/10 border-blue-500/20"
                    />
                    <StatCard
                        icon={ArrowUp} label="Total Outbound" value={fmtBytes(d.bytes_out)}
                        // sub={`${d.bytes_out_gb} GB`}
                        iconColor="text-orange-400" bgColor="bg-orange-500/10 border-orange-500/20"
                    />
                    <StatCard
                        icon={Activity} label="Connections" value={d.connection_count?.toLocaleString() || "-"}
                        iconColor="text-green-400" bgColor="bg-green-500/10 border-green-500/20"
                    />
                    <StatCard
                        icon={Clock} label="Uptime" value={fmtUptime(d.creation_time)}
                        iconColor="text-yellow-400" bgColor="bg-yellow-500/10 border-yellow-500/20"
                    />
                </div>

                {/* Bandwidth Bar */}
                <div className="bg-gray-900/60 border border-gray-700/60 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">Traffic Distribution (RX vs TX)</p>
                        <p className="text-[9px] font-bold text-gray-200">{fmtBytes(totalBytes)} total</p>
                    </div>
                    <div className="h-2.5 w-full bg-gray-700/60 rounded-full overflow-hidden flex">
                        <div className="h-full bg-blue-500 rounded-l-full transition-all" style={{ width: `${rxPct}%` }} />
                        <div className="h-full bg-orange-500 rounded-r-full transition-all" style={{ width: `${txPct}%` }} />
                    </div>
                    <div className="flex justify-between mt-1.5">
                        <span className="text-[9px] text-blue-400 font-bold">↓ RX {rxPct.toFixed(1)}%</span>
                        <span className="text-[9px] text-orange-400 font-bold">TX {txPct.toFixed(1)}% ↑</span>
                    </div>
                </div>

                {/* Tunnel Identifiers */}
                <div className="bg-gray-900/60 border border-gray-700/60 rounded-xl p-4">
                    <div className="flex items-center gap-1.5 mb-3">
                        <Hash size={12} className="text-gray-200" />
                        <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">Tunnel Identifiers</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <p className="text-[9px] text-gray-200 uppercase tracking-widest mb-1">Tunnel ID (IPv4)</p>
                            <p className="font-mono text-[11px] text-cyan-300 bg-cyan-500/5 border border-cyan-500/10 px-2 py-1 rounded">{d.tun_id || "-"}</p>
                        </div>
                        <div>
                            <p className="text-[9px] text-gray-200 uppercase tracking-widest mb-1">Tunnel ID (IPv6)</p>
                            <p className="font-mono text-[11px] text-purple-300 bg-purple-500/5 border border-purple-500/10 px-2 py-1 rounded">{d.tun_id6 || "-"}</p>
                        </div>
                        <div>
                            <p className="text-[9px] text-gray-200 uppercase tracking-widest mb-1">Username / Peer</p>
                            <p className="font-mono text-[11px] text-gray-300 bg-gray-700/40 border border-gray-600/20 px-2 py-1 rounded">{d.username || "-"}</p>
                        </div>
                    </div>
                </div>

                {/* Proxy IDs (Phase 2 SAs) */}
                {d.proxyid.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Layers size={13} className="text-purple-400" />
                            <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest">
                                Phase 2 Security Associations
                            </p>
                            <span className="px-1.5 py-0.5 text-[8px] font-bold bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded">
                                {d.proxyid.length}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {d.proxyid.map((proxy, i) => (
                                <ProxyIdCard key={i} proxy={proxy} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallVpnTunnels({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [tunnels, setTunnels] = useState<VpnTunnelItem[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchTunnels = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            const res = await baseapi.get("/firewall_vpn_tunnels", {
                params: { query: JSON.stringify(queryObj), skip: 0, limit: 100 }
            });
            if (res.data.status) setTunnels(res.data.data);
            else showError("Failed to load VPN tunnels");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchTunnels(); }, [fetchTunnels]);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="bg-purple-600/20 p-1.5 rounded-lg border border-purple-500/30">
                        <Wifi size={16} className="text-purple-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">VPN Tunnels</h1>
                    {!loading && (
                        <span className="text-[10px] font-bold text-gray-200 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded">
                            {tunnels.length} tunnel{tunnels.length !== 1 ? "s" : ""}
                        </span>
                    )}
                </div>
                <button
                    onClick={fetchTunnels}
                    disabled={loading}
                    className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-purple-400"} />
                </button>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-20 gap-2 text-gray-200">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                    <span className="text-[11px] font-bold uppercase tracking-widest">Loading tunnels...</span>
                </div>
            ) : tunnels.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3 opacity-40">
                    <Wifi size={32} className="text-gray-200" />
                    <span className="text-[11px] font-bold uppercase tracking-widest text-gray-200">No VPN tunnels found</span>
                </div>
            ) : (
                <div className="space-y-4">
                    {tunnels.map(item => <TunnelCard key={item.id} item={item} />)}
                </div>
            )}
        </div>
    );
}
